import { useEffect, useMemo, useState } from "react";
import {
    AlertCircle,
    Check,
    Copy,
    KeyRound,
    Plus,
    RotateCcw,
    ShieldAlert,
    ShieldCheck,
    Trash2,
    UserPlus,
    UserX,
} from "lucide-react";
import { formatApiError } from "@/lib/formatApiError";
import { toast } from "sonner";

import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import Card from "@/components/Card";
import ConfirmDialog from "@/components/ConfirmDialog";
import { users as usersApi } from "@/api/endpoints";
import { relativeTime } from "@/lib/format";

/* Inline status pills — sized for table rows. The shared StatusPill
   component is biased toward run-status semantics; for "role" / "active|disabled" /
   "must rotate" badges we want a more neutral palette, so we keep a tiny
   set of utility classes here. */
function Badge({ tone = "neutral", children, testId }) {
    const cls = {
        neutral: "bg-ux-bg-3 text-ux-text-2 border-ux-border-2",
        success: "bg-ux-success-soft text-ux-success border-ux-success/30",
        danger: "bg-ux-danger-soft text-ux-danger border-ux-danger/30",
        warning: "bg-ux-warning-soft text-ux-warning border-ux-warning/30",
        accent: "bg-ux-accent-soft text-ux-accent border-ux-accent/30",
    }[tone] || "bg-ux-bg-3 text-ux-text-2 border-ux-border-2";
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium tabular-nums border ${cls}`}
        >
            {children}
        </span>
    );
}

function CopyField({ value, testId }) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="flex items-center gap-2 rounded-sm bg-ux-bg-2 border border-ux-border-1 px-3 py-2 font-mono text-sm">
            <span data-testid={testId} className="flex-1 truncate text-ux-text-1">{value}</span>
            <button
                type="button"
                onClick={async () => {
                    try {
                        await navigator.clipboard.writeText(value);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1600);
                    } catch (_e) {
                        toast.error("Clipboard not available — copy manually.");
                    }
                }}
                className="text-ux-text-3 hover:text-ux-text-1 transition-colors"
                data-testid={`${testId}-copy`}
                aria-label="Copy to clipboard"
            >
                {copied ? <Check className="h-4 w-4 text-ux-success" /> : <Copy className="h-4 w-4" />}
            </button>
        </div>
    );
}

export default function UsersAdminPage() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const refresh = async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await usersApi.list();
            setUsers(list || []);
        } catch (err) {
            setError(formatApiError(err, "Failed to load users."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const adminCount = useMemo(
        () => users.filter((u) => u.role === "admin" && !u.disabled).length,
        [users],
    );

    const onPatch = async (username, payload) => {
        try {
            await usersApi.patch(username, payload);
            toast.success(`Updated ${username}`);
            refresh();
        } catch (err) {
            toast.error(formatApiError(err, "Update failed."));
        }
    };

    const onDelete = async (username) => {
        try {
            await usersApi.remove(username);
            toast.success(`Deleted ${username}`);
            setDeleteTarget(null);
            refresh();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed."));
            setDeleteTarget(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="users-admin-page">
            <header className="flex items-end justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-ux-text-3">
                        Admin
                    </div>
                    <h1 className="text-2xl font-semibold text-ux-text-1">Users</h1>
                    <p className="text-sm text-ux-text-3 mt-0.5">
                        {users.length} user{users.length === 1 ? "" : "s"} · {adminCount} active admin
                        {adminCount === 1 ? "" : "s"}
                    </p>
                </div>
                <Button
                    onClick={() => setCreateOpen(true)}
                    data-testid="users-admin-new-button"
                    className="bg-ux-accent text-ux-bg-0 hover:bg-ux-accent/90"
                >
                    <Plus className="h-4 w-4 mr-1" /> New user
                </Button>
            </header>

            {error && (
                <div className="flex items-start gap-2 rounded-sm border border-ux-danger/40 bg-ux-danger-soft px-3 py-2 text-sm text-ux-danger">
                    <ShieldAlert className="h-4 w-4 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            <Card testId="users-admin-table-card">
                <div className="overflow-x-auto -mx-5 -my-4">
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wider text-ux-text-3">
                            <tr className="border-b border-ux-border-1">
                                <th className="text-left px-4 py-2 font-medium">Username</th>
                                <th className="text-left px-4 py-2 font-medium">Role</th>
                                <th className="text-left px-4 py-2 font-medium">Status</th>
                                <th className="text-left px-4 py-2 font-medium">Must rotate</th>
                                <th className="text-left px-4 py-2 font-medium">Last login</th>
                                <th className="text-left px-4 py-2 font-medium">Created</th>
                                <th className="text-right px-4 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ux-border-1">
                            {loading && (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-ux-text-3">
                                        Loading…
                                    </td>
                                </tr>
                            )}
                            {!loading && users.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-ux-text-3">
                                        No users yet.
                                    </td>
                                </tr>
                            )}
                            {!loading
                                && users.map((u) => {
                                    const isSelf = currentUser?.username === u.username;
                                    return (
                                        <tr key={u.id} className="hover:bg-ux-bg-2/40" data-testid={`user-row-${u.username}`}>
                                            <td className="px-4 py-2.5">
                                                <div className="font-mono text-ux-text-1">{u.username}</div>
                                                {isSelf && (
                                                    <div className="text-[10px] uppercase tracking-wider text-ux-accent mt-0.5">
                                                        you
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <Select
                                                    value={u.role}
                                                    onValueChange={(v) => onPatch(u.username, { role: v })}
                                                >
                                                    <SelectTrigger
                                                        data-testid={`user-role-select-${u.username}`}
                                                        className="h-8 w-[120px] bg-ux-bg-2 border-ux-border-1 text-ux-text-1"
                                                    >
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="admin">admin</SelectItem>
                                                        <SelectItem value="operator">operator</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                            <td className="px-4 py-2.5">
                                                {u.disabled ? (
                                                    <Badge tone="danger" testId={`user-status-${u.username}`}>disabled</Badge>
                                                ) : (
                                                    <Badge tone="success" testId={`user-status-${u.username}`}>active</Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                {u.must_change_password ? (
                                                    <Badge tone="warning" testId={`user-mustrotate-${u.username}`}>yes</Badge>
                                                ) : (
                                                    <span className="text-ux-text-3">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5 text-ux-text-2 text-xs">
                                                {u.last_login_at ? relativeTime(u.last_login_at) : "—"}
                                            </td>
                                            <td className="px-4 py-2.5 text-ux-text-2 text-xs">
                                                {relativeTime(u.created_at)}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex justify-end gap-1.5">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        title={u.disabled ? "Enable" : "Disable"}
                                                        onClick={() => onPatch(u.username, { disabled: !u.disabled })}
                                                        data-testid={`user-toggle-disable-${u.username}`}
                                                        className="h-7 w-7 p-0 text-ux-text-3 hover:text-ux-text-1 hover:bg-ux-bg-2"
                                                    >
                                                        {u.disabled ? <ShieldCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        title="Reset password"
                                                        onClick={() => setResetTarget(u.username)}
                                                        data-testid={`user-reset-${u.username}`}
                                                        className="h-7 w-7 p-0 text-ux-text-3 hover:text-ux-text-1 hover:bg-ux-bg-2"
                                                    >
                                                        <RotateCcw className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        title={isSelf ? "Cannot delete self" : "Delete"}
                                                        disabled={isSelf}
                                                        onClick={() => setDeleteTarget(u.username)}
                                                        data-testid={`user-delete-${u.username}`}
                                                        className="h-7 w-7 p-0 text-ux-text-3 hover:text-ux-danger hover:bg-ux-danger-soft disabled:opacity-30"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </Card>

            <CreateUserDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={() => {
                    setCreateOpen(false);
                    refresh();
                }}
            />

            <ResetPasswordDialog
                username={resetTarget}
                onClose={() => setResetTarget(null)}
                onDone={() => {
                    setResetTarget(null);
                    refresh();
                }}
            />

            <ConfirmDialog
                open={Boolean(deleteTarget)}
                title={deleteTarget ? `Delete ${deleteTarget}?` : ""}
                message="This is permanent. Their JWTs are invalidated and any pending account state is wiped."
                confirmLabel="Delete user"
                variant="danger"
                onConfirm={() => deleteTarget && onDelete(deleteTarget)}
                onCancel={() => setDeleteTarget(null)}
                testId="users-admin-delete-confirm"
            />
        </div>
    );
}

/* ──────────────────────────── Create user ──────────────────────────── */
function CreateUserDialog({ open, onOpenChange, onCreated }) {
    const [username, setUsername] = useState("");
    const [role, setRole] = useState("operator");
    const [tempPw, setTempPw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    useEffect(() => {
        if (!open) {
            setUsername("");
            setRole("operator");
            setTempPw("");
            setErrorMsg(null);
            setSubmitting(false);
        }
    }, [open]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrorMsg(null);
        try {
            await usersApi.create({
                username: username.trim().toLowerCase(),
                role,
                password: tempPw,
            });
            toast.success(`User ${username} created. Share the temporary password securely.`);
            onCreated();
        } catch (err) {
            setErrorMsg(formatApiError(err, "Could not create user."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-ux-bg-1 border-ux-border-1 text-ux-text-1 max-w-[440px]"
                data-testid="users-admin-create-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-ux-text-1">
                        <UserPlus className="h-4 w-4 text-ux-accent" /> New user
                    </DialogTitle>
                    <DialogDescription className="text-ux-text-3">
                        The user must change their password on first sign-in.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-3 pt-2">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-ux-text-3">
                            Username
                        </Label>
                        <Input
                            data-testid="users-admin-create-username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="operator-1"
                            className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1"
                            autoFocus
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-ux-text-3">
                            Role
                        </Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger
                                data-testid="users-admin-create-role"
                                className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="operator">operator</SelectItem>
                                <SelectItem value="admin">admin</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-ux-text-3">
                            Temporary password
                        </Label>
                        <Input
                            data-testid="users-admin-create-password"
                            type="text"
                            value={tempPw}
                            onChange={(e) => setTempPw(e.target.value)}
                            placeholder="Initial-2025!"
                            className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 font-mono"
                        />
                        {tempPw && (
                            <CopyField value={tempPw} testId="users-admin-create-temppw-display" />
                        )}
                        <p className="text-[11px] text-ux-text-3">
                            ≥ 10 chars, contains a letter and a digit, not a common password.
                        </p>
                    </div>
                    {errorMsg && (
                        <div className="flex items-start gap-2 rounded-sm border border-ux-danger/40 bg-ux-danger-soft px-3 py-2 text-sm text-ux-danger">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <span>{errorMsg}</span>
                        </div>
                    )}
                    <DialogFooter className="pt-1">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            className="text-ux-text-3 hover:text-ux-text-1 hover:bg-ux-bg-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="users-admin-create-submit"
                            disabled={!username || !tempPw || submitting}
                            className="bg-ux-accent text-ux-bg-0 hover:bg-ux-accent/90 disabled:opacity-50"
                        >
                            {submitting ? "Creating…" : "Create user"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

/* ──────────────────────────── Reset password ───────────────────────── */
function ResetPasswordDialog({ username, onClose, onDone }) {
    const [tempPw, setTempPw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    useEffect(() => {
        if (!username) {
            setTempPw("");
            setErrorMsg(null);
            setSubmitting(false);
        }
    }, [username]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrorMsg(null);
        try {
            await usersApi.resetPassword(username, tempPw);
            toast.success(`Password reset for ${username}.`);
            onDone();
        } catch (err) {
            setErrorMsg(formatApiError(err, "Reset failed."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={Boolean(username)} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="bg-ux-bg-1 border-ux-border-1 text-ux-text-1 max-w-[440px]"
                data-testid="users-admin-reset-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-ux-text-1">
                        <KeyRound className="h-4 w-4 text-ux-warning" /> Reset password
                    </DialogTitle>
                    <DialogDescription className="text-ux-text-3">
                        Sets a new temporary password for{" "}
                        <span className="font-mono text-ux-text-2">{username}</span> and forces them
                        to rotate at next sign-in. All existing sessions are invalidated.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-3 pt-2">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-ux-text-3">
                            New temporary password
                        </Label>
                        <Input
                            data-testid="users-admin-reset-password"
                            type="text"
                            value={tempPw}
                            onChange={(e) => setTempPw(e.target.value)}
                            className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 font-mono"
                        />
                        {tempPw && <CopyField value={tempPw} testId="users-admin-reset-display" />}
                    </div>
                    {errorMsg && (
                        <div className="flex items-start gap-2 rounded-sm border border-ux-danger/40 bg-ux-danger-soft px-3 py-2 text-sm text-ux-danger">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <span>{errorMsg}</span>
                        </div>
                    )}
                    <DialogFooter className="pt-1">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            className="text-ux-text-3 hover:text-ux-text-1 hover:bg-ux-bg-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="users-admin-reset-submit"
                            disabled={!tempPw || submitting}
                            className="bg-ux-warning text-ux-bg-0 hover:bg-ux-warning/90 disabled:opacity-50"
                        >
                            {submitting ? "Resetting…" : "Reset password"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
